/**
 * music.js — looping background score.
 *
 * Sky-Drop's sound effects are synthesised from oscillators at runtime, which
 * is why the game ships no audio files. Music is the one thing that approach
 * cannot do: a Web Audio arpeggio reads as a chiptune, and the game does not
 * look like a chiptune. So the two tracks are real recordings, and this module
 * exists to make two ~2MB files behave like game music rather than like a page
 * playing an MP3.
 *
 * Three problems it solves, in order of how badly they show:
 *
 * 1. LOOPING. A generated track does not loop. Even asked for constant energy
 *    and no fade, its last sample does not line up with its first, so an
 *    `audio.loop = true` restart is an audible click and a lurch every couple
 *    of minutes. Instead each track is scheduled as two overlapping buffer
 *    sources that crossfade into each other, so the seam is a blend rather than
 *    a cut. See scheduleLoop.
 *
 * 2. LOADING. Music is two orders of magnitude larger than everything else the
 *    game loads. It is fetched lazily, per track, and the fetch never blocks
 *    anything — if it fails or is slow the game is simply quiet, which is the
 *    correct failure mode for background audio.
 *
 * 3. AUTOPLAY. Browsers refuse audio until a user gesture. The context is
 *    already unlocked on the Start button (see sound.js initAudio), and every
 *    entry point here goes through the same shared graph, so music inherits
 *    that unlock and the global mute for free.
 */

import { getAudioGraph } from './sound';

import menuTrack from './assets/music/menu.mp3';
import fightTrack from './assets/music/fight.mp3';

const TRACKS = { menu: menuTrack, fight: fightTrack };

/**
 * Music level relative to the effects bus.
 *
 * Well under the effects: the score is there to be underneath a fight, and a
 * beam or a kick has to cut through it without the player straining. Tuned by
 * ear against the loudest effect (vaporize) rather than in isolation.
 */
const MUSIC_GAIN = 0.34;

/**
 * Crossfade length in seconds, used for BOTH the internal loop seam and for
 * changing tracks. Long enough to hide a mismatched loop point, short enough
 * that switching from menu to fight still feels like a cue.
 */
const CROSSFADE_S = 1.8;

/** Fade applied when music is stopped outright (returning to the menu, a win). */
const STOP_FADE_S = 0.6;

// Decoded audio, keyed by track name. Decoding is the expensive part, so a
// track that has been heard once switches back instantly.
const buffers = new Map();
// In-flight loads, so rapid screen changes cannot start the same fetch twice.
const loading = new Map();

// The currently playing voice: { name, gain, sources[], timer }.
let current = null;
// Set before any load finishes, so a track requested and then superseded while
// still downloading never starts playing over the top of its replacement.
let wanted = null;

/**
 * Fetch and decode one track.
 *
 * @param {string} name Key into TRACKS.
 * @returns {Promise<AudioBuffer|null>} null if unavailable or undecodable.
 */
async function load(name) {
  if (buffers.has(name)) return buffers.get(name);
  if (loading.has(name)) return loading.get(name);

  const graph = getAudioGraph();
  if (!graph || !TRACKS[name]) return null;

  const task = fetch(TRACKS[name])
    .then((res) => res.arrayBuffer())
    .then((data) => graph.ctx.decodeAudioData(data))
    .then((buffer) => {
      buffers.set(name, buffer);
      return buffer;
    })
    .catch((err) => {
      // Background music is never worth breaking the game over.
      console.warn('Music failed to load:', name, err);
      return null;
    })
    .finally(() => loading.delete(name));

  loading.set(name, task);
  return task;
}

/**
 * Schedule one pass of a track, and arrange the next one to overlap it.
 *
 * This is the loop. Each pass starts CROSSFADE_S before the previous one ends
 * and fades in as that one fades out, so the join is a blend of the track's
 * tail with its own head instead of a hard cut back to sample zero. The pass
 * after that is scheduled by a timer rather than by an `onended` handler,
 * because `onended` fires only once the source has fully stopped — which is
 * already too late to overlap anything.
 *
 * @param {object} voice The voice record to schedule into.
 * @param {AudioBuffer} buffer Decoded track.
 * @param {number} startAt Context time to begin this pass.
 */
function scheduleLoop(voice, buffer, startAt) {
  const graph = getAudioGraph();
  if (!graph || voice.stopped) return;

  const source = graph.ctx.createBufferSource();
  source.buffer = buffer;

  // Per-pass gain, so one pass can fade out while the next fades in. A single
  // shared gain node could not do both at once.
  const passGain = graph.ctx.createGain();
  const full = 1;
  const fade = Math.min(CROSSFADE_S, buffer.duration / 3);
  const end = startAt + buffer.duration;

  passGain.gain.setValueAtTime(voice.firstPass ? full : 0, startAt);
  if (!voice.firstPass) {
    passGain.gain.linearRampToValueAtTime(full, startAt + fade);
  }
  // Fade this pass out across the window the next one fades in over.
  passGain.gain.setValueAtTime(full, end - fade);
  passGain.gain.linearRampToValueAtTime(0, end);

  source.connect(passGain);
  passGain.connect(voice.gain);
  source.start(startAt);
  source.stop(end + 0.05);

  voice.sources.push(source);
  // Drop finished sources so a long session does not accumulate them.
  source.onended = () => {
    voice.sources = voice.sources.filter((s) => s !== source);
  };

  voice.firstPass = false;
  // Queue the next pass to begin one crossfade before this one ends. Timer is
  // in wall-clock ms relative to now; the schedule itself is sample-accurate.
  const nextAt = end - fade;
  const delayMs = Math.max(0, (nextAt - graph.ctx.currentTime) * 1000);
  voice.timer = setTimeout(() => scheduleLoop(voice, buffer, nextAt), delayMs);
}

/**
 * Fade a voice out and tear it down.
 *
 * @param {object} voice
 * @param {number} seconds Fade length.
 */
function retire(voice, seconds) {
  if (!voice || voice.stopped) return;
  voice.stopped = true;
  clearTimeout(voice.timer);

  const graph = getAudioGraph();
  if (!graph) return;
  const now = graph.ctx.currentTime;
  voice.gain.gain.cancelScheduledValues(now);
  voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
  voice.gain.gain.linearRampToValueAtTime(0, now + seconds);

  const sources = voice.sources.slice();
  setTimeout(() => {
    sources.forEach((s) => {
      try {
        s.stop();
      } catch (err) {
        // Already stopped — nothing to do.
      }
    });
  }, seconds * 1000 + 60);
}

/**
 * Play a track, crossfading from whatever is playing now.
 *
 * Safe to call every render: asking for the track that is already playing does
 * nothing, which is what lets callers simply state which track the current
 * screen wants rather than tracking transitions themselves.
 *
 * @param {'menu'|'fight'} name
 */
export function playMusic(name) {
  if (wanted === name && current && !current.stopped) return;
  wanted = name;

  const graph = getAudioGraph();
  if (!graph) return;

  load(name).then((buffer) => {
    // Superseded while downloading, or the load failed.
    if (!buffer || wanted !== name) return;
    if (current && current.name === name && !current.stopped) return;

    if (current) retire(current, CROSSFADE_S);

    const gain = graph.ctx.createGain();
    gain.gain.value = MUSIC_GAIN;
    gain.connect(graph.master);

    const voice = { name, gain, sources: [], timer: null, firstPass: true, stopped: false };
    current = voice;
    // Nudge the start fractionally into the future: scheduling at exactly
    // currentTime can drop the first buffer on a busy frame.
    scheduleLoop(voice, buffer, graph.ctx.currentTime + 0.05);
  });
}

/** Fade the score out. Called when a match ends or the player leaves it. */
export function stopMusic() {
  wanted = null;
  if (current) retire(current, STOP_FADE_S);
  current = null;
}

/**
 * Warm the cache for a track without playing it, so the cue it is wanted for
 * is not preceded by a silent download.
 *
 * @param {'menu'|'fight'} name
 */
export function preloadMusic(name) {
  load(name);
}
